import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ClockIcon, CheckCircleIcon, TruckIcon, ChefHatIcon, PhoneIcon } from 'lucide-react';
const OrderTracking: React.FC = () => {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const [currentStatus, setCurrentStatus] = useState<number>(1);
  // Mock statuses
  const statuses = [{
    id: 1,
    name: 'Order Received',
    icon: CheckCircleIcon,
    time: '11:30 AM'
  }, {
    id: 2,
    name: 'Preparing',
    icon: ChefHatIcon,
    time: '11:35 AM'
  }, {
    id: 3,
    name: 'Out for Delivery',
    icon: TruckIcon,
    time: '12:05 PM'
  }, {
    id: 4,
    name: 'Delivered',
    icon: CheckCircleIcon,
    time: '12:30 PM'
  }];
  // Mock delivery person
  const deliveryPerson = {
    name: 'Michael Chen',
    phone: '+65 9123 4567',
    photo: 'https://randomuser.me/api/portraits/men/32.jpg'
  };
  // Simulate order status progression
  useEffect(() => {
    if (currentStatus < statuses.length) {
      const timer = setTimeout(() => {
        setCurrentStatus(prev => prev + 1);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [currentStatus, statuses.length]);
  return <div className="max-w-2xl mx-auto" data-id="element-832">
      <div className="bg-white rounded-lg shadow-md overflow-hidden" data-id="element-833">
        {/* Header */}
        <div className="p-4 bg-gray-50 border-b" data-id="element-834">
          <div className="flex justify-between items-center" data-id="element-835">
            <h1 className="font-semibold" data-id="element-836">Order #{id || '123456'}</h1>
            <div className="text-sm text-gray-600" data-id="element-837">Placed at 11:30 AM</div>
          </div>
        </div>
        {/* Estimated delivery time */}
        <div className="p-4 border-b flex items-center" data-id="element-838">
          <div className="w-10 h-10 bg-[#E84118] bg-opacity-10 rounded-full flex items-center justify-center mr-3" data-id="element-839">
            <ClockIcon size={20} className="text-[#E84118]" data-id="element-840" />
          </div>
          <div data-id="element-841">
            <div className="text-sm text-gray-600" data-id="element-842">Estimated Delivery Time</div>
            <div className="font-semibold" data-id="element-843">12:15 PM - 12:45 PM</div>
          </div>
        </div>
        {/* Status tracker */}
        <div className="p-4" data-id="element-844">
          <div className="relative" data-id="element-845">
            {statuses.map((status, index) => {
            const isActive = currentStatus >= status.id;
            const Icon = status.icon;
            return <div key={status.id} className="flex mb-8 last:mb-0" data-id="element-846">
                  {/* Status icon */}
                  <div className="relative" data-id="element-847">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isActive ? 'bg-[#E84118]' : 'bg-gray-200'}`} data-id="element-848">
                      <Icon size={16} className={isActive ? 'text-white' : 'text-gray-500'} data-id="element-849" />
                    </div>
                    {/* Connecting line */}
                    {index < statuses.length - 1 && <div className={`absolute top-8 left-4 w-0.5 h-8 -ml-px ${currentStatus > status.id ? 'bg-[#E84118]' : 'bg-gray-200'}`} data-id="element-850"></div>}
                  </div>
                  {/* Status details */}
                  <div className="ml-4" data-id="element-851">
                    <div className={`font-medium ${isActive ? 'text-[#E84118]' : 'text-gray-500'}`} data-id="element-852">
                      {status.name}
                    </div>
                    <div className="text-sm text-gray-500" data-id="element-853">
                      {isActive ? status.time : ''}
                    </div>
                    {/* Delivery person info */}
                    {status.id === 3 && isActive && currentStatus < 4 && <div className="mt-2 p-3 bg-blue-50 rounded-md" data-id="element-854">
                        <div className="flex items-center" data-id="element-855">
                          <div className="w-10 h-10 rounded-full bg-gray-300 overflow-hidden mr-3" data-id="element-856">
                            <img src={deliveryPerson.photo} alt={deliveryPerson.name} className="w-full h-full object-cover" data-id="element-857" />
                          </div>
                          <div data-id="element-858">
                            <div className="font-medium" data-id="element-859">
                              {deliveryPerson.name}
                            </div>
                            <div className="text-sm text-gray-600" data-id="element-860">
                              Delivery Partner
                            </div>
                          </div>
                          <div className="ml-auto" data-id="element-861">
                            <a href={`tel:${deliveryPerson.phone}`} className="flex items-center justify-center w-10 h-10 bg-[#E84118] rounded-full text-white" data-id="element-862">
                              <PhoneIcon size={18} data-id="element-863" />
                            </a>
                          </div>
                        </div>
                      </div>}
                  </div>
                </div>;
          })}
          </div>
        </div>
        {/* Order details */}
        <div className="p-4 bg-gray-50 border-t" data-id="element-864">
          <div className="flex justify-between items-center mb-2" data-id="element-865">
            <h2 className="font-medium" data-id="element-866">Order Details</h2>
            <button className="text-[#E84118] text-sm" data-id="element-867">View All</button>
          </div>
          <div className="space-y-3" data-id="element-868">
            <div className="flex justify-between items-center" data-id="element-869">
              <div className="flex items-center" data-id="element-870">
                <div className="w-10 h-10 bg-gray-200 rounded overflow-hidden mr-3" data-id="element-871">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/rd7ZuDhGdqSuEjdsymkVFA/image.png" alt="Food item" className="w-full h-full object-cover" data-id="element-872" />
                </div>
                <div data-id="element-873">
                  <div className="font-medium" data-id="element-874">Fried Egg with Tomato</div>
                  <div className="text-sm text-gray-500" data-id="element-875">x2</div>
                </div>
              </div>
              <div data-id="element-876">$15.00</div>
            </div>
            <div className="flex justify-between items-center" data-id="element-877">
              <div className="flex items-center" data-id="element-878">
                <div className="w-10 h-10 bg-gray-200 rounded overflow-hidden mr-3" data-id="element-879">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/rd7ZuDhGdqSuEjdsymkVFA/image.png" alt="Food item" className="w-full h-full object-cover" data-id="element-880" />
                </div>
                <div data-id="element-881">
                  <div className="font-medium" data-id="element-882">Fried Egg with Tomato</div>
                  <div className="text-sm text-gray-500" data-id="element-883">x1</div>
                </div>
              </div>
              <div data-id="element-884">$7.50</div>
            </div>
          </div>
          <div className="mt-4 pt-3 border-t border-gray-200 flex justify-between font-medium" data-id="element-885">
            <span data-id="element-886">Total</span>
            <span data-id="element-887">$22.50</span>
          </div>
        </div>
      </div>
    </div>;
};
export default OrderTracking;